import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';

import {
    getCategory,
    getGridElements,
    getCategoryAttachments,
    getFilters,
    getMenuCategory,
    getIsLoaded,
    getSeoCategoryName,
    getElementDefaultImageTypes,
} from '../../../store/category-page.selectors';
import { GridDefault, GridElement, GridStudio } from '../models/grid.models';
import { InitCategoryGrid, LoadNextPageGrid, LoadNextPageStudio } from '../store/category-grid.actions';
import {
    getGridPageAllIds,
    getPageGridElements,
    getPageProductIds,
    getTotalQuantityProducts,
    getNewCollectionDefault,
    getNewCollectionStudio,
} from '../store/category-grid.selectors';

import { Category } from '@core/domain/models/category.models';
import { GridElemType } from '@core/domain/types/product.types';
import { getLanguageisRTL, getXConfigValue, getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { findCategoryById, getAttachmentByPath } from '@features/category/application/services/category.utils';
import { ProductsArrayPublicSelectors } from '@features/products/productsArray';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { NotificationService, PublicNotificationTypeEnum } from '@layout/ui/components/notification';
import { CategoryFilterState } from '@pages/category/public_api';
import { GridTypeViewService } from '@pages/category/services/grid-type-view.service';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';
import { GridTypes } from '@shared/models/grid-type.model';
import { GoToCategory } from '@shared/navigation';

@Component({
    selector: 'category-grid',
    templateUrl: './category-grid.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryGridComponent implements OnInit, OnDestroy {
    private readonly notificationService = inject(NotificationService);

    public readonly isLoadedCategory$: Observable<boolean> = this.store.select(getIsLoaded);

    public readonly isRtl$: Observable<boolean> = this.store.select(getLanguageisRTL);

    public readonly seoCategoryName$: Observable<string> = this.store.select(getSeoCategoryName);

    public readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    public showLastSizesGrid: boolean = this.abTestingService.isShowLastSizes;

    public readonly gridType$: Observable<GridTypes> = this.gridTypeViewService.getGridTypeStore$();

    public readonly getGridElements$: Observable<GridElement[]> = this.store.select(getGridElements).pipe(
        startWith([]),
    );

    private readonly getAllGridPageIds$: Observable<number[]> = this.store.select(getGridPageAllIds).pipe(
        startWith([]),
    );

    public elementDefaultImageTypes$: Observable<Map<number, string>> = this.store.select(getElementDefaultImageTypes);

    public categoryAttachments$: Observable<any> = this.store.select(getCategoryAttachments);

    public readonly getPageProductIds$: Observable<number[]> = combineLatest([
        this.gridType$,
        this.categoryAttachments$,
        this.store.select(ProductsArrayPublicSelectors.getProductsList),
        this.store.select(getPageProductIds),
        this.store.select(getXConfigValue('SHOW_PRODUCT_CATEGORY_SKU_NO_STOCK')),
    ]).pipe(
        map(([gridType, attachments, productsList, pageProductIds, showNoStock]) => {
            const showCategoryBannersZoom2 = !!attachments?.find((attachment: any) => attachment.name === 'SHOW_BANNERS_ZOOM2');
            const showCategoryBannersZoom3 = !!attachments?.find((attachment: any) => attachment.name === 'SHOW_BANNERS_ZOOM3');

            const showBanners = gridType === GridTypes.GRID_4 && showCategoryBannersZoom2
                            || gridType === GridTypes.GRID_16 && showCategoryBannersZoom3
                            || gridType === GridTypes.ZARA_STUDIO;

            return pageProductIds?.filter((id) => {
                const isNotBanner = productsList[id]?.gridElemType !== GridElemType.BANNER;
                const isHiddenOrSoldOut = ['HIDDEN', 'SOLD_OUT'].includes(productsList[id]?.visibilityValue) && showNoStock === 'HIDDEN';
                return (isNotBanner || showBanners) && !isHiddenOrSoldOut;
            });
        })
    );

    public menuCategory$: Observable<any> = this.store.select(getMenuCategory);

    public getFilters$: Observable<CategoryFilterState> = this.store.select(getFilters);

    public filtersApplied$: Observable<boolean> = this.getFilters$
        .pipe(map(filters => filters.isApplied));

    public enableLastSizes$: Observable<boolean> = this.store.select(getCmsConfigValue('show_last_sizes'));

    public showNoResults$: Observable<boolean> = this.store.select(getTotalQuantityProducts)
        .pipe(filter(totalQuantity => !!totalQuantity || totalQuantity === 0), map((totalQuantity) => {
            return totalQuantity === 0;
        }));

    public readonly mediaCarouselActive$: Observable<boolean> = combineLatest([
        this.gridType$,
        this.categoryAttachments$,
        this.store.select(getCmsConfigValue('isMediaCarouselActivatedGrid')),
        this.store.select(getCmsConfigValue('isMediaCarouselActivatedGridV2')),
        this.store.select(getCmsConfigValue('isMediaCarouselActivatedGridV3')),
        this.store.select(getResolution).pipe(
            map((resolutions: Resolutions) => resolutions === Resolutions.MOBILE)
        ),
    ]).pipe(
        map(([gridtype, attachments, activeGridStudio, activeGridV2, activeGridV3, isMobile]) => {
            if (attachments?.some((attachment: any) => attachment.name === 'NO_ACTIVE_SWIPE')) return false;

            const gridConfig = {
                [GridTypes.ZARA_STUDIO]: activeGridStudio,
                [GridTypes.GRID_4]: activeGridV2,
                [GridTypes.GRID_16]: activeGridV3,
            }[gridtype];

            const configValue = isMobile ? gridConfig?.mobile : gridConfig?.desktop;
            return configValue === true || configValue === 'true';
        })
    );

    public getClassGrid$: Observable<string> = combineLatest([
        this.menuCategory$.pipe(
            filter((menuCategory) => !!menuCategory?.categories && !!menuCategory?.currentCategory),
            map((menuCategory): boolean =>
                findCategoryById(menuCategory.categories, menuCategory.currentCategory.id)?.level > 1)
        ),
        this.categoryAttachments$,
        this.filtersApplied$,
        this.isRtl$,
    ]).pipe(
        map(([hasSibling, attachments, filters, isRtl]) => {
            const showLimitedChildCategories = !!attachments?.find((attachment: any) => attachment.name === 'show_subcategories_grid');
            const specialColection = !!attachments?.find((attachment: any) => attachment.name === 'special_collection');
            let className = '';
            if (hasSibling && showLimitedChildCategories) { className = `${className} with-sibling`; }
            if (showLimitedChildCategories) { className = `${className} with-subcategories`; }
            if (specialColection) { className = `${className} js-with-special-collection`; }
            if (filters) { className = `${className} with-filter-badgets`; }
            if (isRtl) { className = `${className} is-rtl`; }
            return className;
        })
    );

    public enableNewCollection$: Observable<boolean> = combineLatest([
        this.store.select(getNewCollectionDefault),
        this.store.select(getNewCollectionStudio),
        this.getFilters$,
    ]).pipe(
        map(([gridNewCollectionDefault, gridNewCollectionStudio, filters]) => {
            return this.isNewCollectionEnabled(gridNewCollectionDefault, gridNewCollectionStudio, filters);
        })
    );

    public enabledDiscountDisclaimer$: Observable<boolean> = combineLatest([
        this.store.select(ProductsArrayPublicSelectors.getProductsList),
        this.getAllGridPageIds$,
    ]).pipe(
        map(([productsList, allGridPageIds]) => {
            return !!Object.values(productsList).find((product) => {
                const sizes = product?.status?.selectedColor?.sizes;
                const hasPromotion = sizes?.length > 0 && !!sizes[0]?.promotionId;
                return hasPromotion && allGridPageIds.includes(Number(product.id));
            });
        })
    );

    public pageGridElements$: Observable<GridElement[]> = this.store.select(getPageGridElements).pipe(
        startWith([]),
    );

    public isCategoryNew$: Observable<boolean> = combineLatest([
        this.store.select(getCmsConfigValue('exclude_category_tag_new')),
        this.getCategory$,
    ]).pipe(
        map(([excludeCategoryTag, category]) => {
            if (excludeCategoryTag && category) {
                return excludeCategoryTag.some(tag => category?.key?.includes(tag));
            }
            return false;
        })
    );

    constructor(
        private readonly store: Store,
        private readonly abTestingService: ABTestingService,
        private readonly gridTypeViewService: GridTypeViewService,
    ) { }

    public ngOnInit(): void {
        this.store.dispatch(InitCategoryGrid());
        this.showNotificationCategoryBanner();
    }

    public showNotificationCategoryBanner(): void {
        this.isLoadedCategory$
            .pipe(
                filter((isLoaded: boolean) => isLoaded),
                switchMap(() =>
                    combineLatest([
                        this.getCategory$,
                        this.store.select(getCmsConfigValue('category_banner')),
                    ]).pipe(
                        map(([category, bannerConfig]) => {
                            const categoryBanner = getAttachmentByPath(category, AttachmentsEnums.CATEGORY_BANNER);
                            if (categoryBanner) {
                                const config = bannerConfig?.find((conf) => conf?.IdBanner?.includes(category.id));
                                this.notificationService.openNotification({
                                    title: config?.TITLE,
                                    type: PublicNotificationTypeEnum.BANNER,
                                    message: config?.TEXT,
                                    time: 0,
                                    styles: {
                                        backgroundColor: config?.background_color,
                                        textColor: config?.text_color,
                                    },
                                    action: () => {
                                        this.store.dispatch(GoToCategory({ categoryId: config?.IdRedirect.toString() }));
                                    },
                                });
                            } else {
                                this.notificationService.closeNotification();
                            }
                        })
                    ))
            )
            // eslint-disable-next-line rxjs/no-ignored-subscribe
            .subscribe();
    }

    public onScrollGridDefault(): void {
        this.store.dispatch(LoadNextPageGrid());
    }

    public onScrollGridStudio(): void {
        this.store.dispatch(LoadNextPageStudio());
    }

    private isNewCollectionEnabled(
        gridNewCollectionDefault: GridDefault,
        gridNewCollectionStudio: GridStudio,
        filters: CategoryFilterState
    ): boolean {
        const gridInitialized = !!gridNewCollectionDefault || !!gridNewCollectionStudio;
        return filters.isApplied || filters.order.isApplied ? false : !!gridInitialized;
    }

    public ngOnDestroy(): void {
        this.notificationService.closeNotification();
    }
}
